import { MAX_GEOJSON_BYTES } from './constants'
import { ArchiveError } from './errors'
import type { DocPoint } from './features'
import { isDocumentationGeojson, isIgnoredEntry, isImageName, type PhotoRef } from './imageIndex'
import { decodeLzmaHeader } from './lzmaHeader'
import { pointsFromGeojson } from './pointColumns'
import { isSevenZip, readSevenZipIndex, type HeaderDecoder } from './sevenZip'
import { readZipEntryBlob, readZipIndex } from './zip'

const NESTED_RE = /\.(7z|rar|zip|tar|gz|tgz)$/i

export type OpenedDocumentation = {
  file: File
  points: DocPoint[]
  imageCount: number
}

export type PointBuilder = (bytes: Uint8Array, images: PhotoRef[]) => DocPoint[] | Promise<DocPoint[]>

const readGeojsonBytes = async (file: Blob, entry: PhotoRef): Promise<Uint8Array> => {
  if (entry.kind === 'packed') throw new ArchiveError('compressed')
  if (entry.size > MAX_GEOJSON_BYTES || entry.uncompressedSize > MAX_GEOJSON_BYTES)
    throw new ArchiveError('geojson-too-large')
  const blob =
    entry.kind === 'raw'
      ? file.slice(entry.offset, entry.offset + entry.size)
      : await readZipEntryBlob(file, entry, 'geojson-too-large')
  if (blob.size > MAX_GEOJSON_BYTES) throw new ArchiveError('geojson-too-large')
  return new Uint8Array(await blob.arrayBuffer())
}

export async function listArchive(file: Blob, decodeHeader: HeaderDecoder = decodeLzmaHeader): Promise<PhotoRef[]> {
  const magic = new Uint8Array(await file.slice(0, 6).arrayBuffer())
  if (isSevenZip(magic)) return readSevenZipIndex(file, decodeHeader)
  if (magic[0] === 0x50 && magic[1] === 0x4b) return readZipIndex(file)
  throw new ArchiveError('not-archive', file instanceof File ? file.name : undefined)
}

export async function openDocumentationArchive(
  file: File,
  options?: {
    decodeHeader?: HeaderDecoder
    buildPoints?: PointBuilder
  }
): Promise<OpenedDocumentation> {
  const entries = (await listArchive(file, options?.decodeHeader)).filter(entry => !isIgnoredEntry(entry.name))
  const images = entries.filter(entry => isImageName(entry.name))
  const geojson = entries
    .filter(entry => isDocumentationGeojson(entry.name))
    .sort((a, b) => a.name.split('/').filter(Boolean).length - b.name.split('/').filter(Boolean).length)[0]
  if (!images.length) {
    const nested = entries.find(entry => NESTED_RE.test(entry.name))
    if (nested) throw new ArchiveError('nested', nested.name)
    if (entries.some(entry => entry.kind === 'packed')) throw new ArchiveError('compressed')
    throw new ArchiveError('no-photos', file.name)
  }
  if (!geojson) throw new ArchiveError('no-geojson')
  const bytes = await readGeojsonBytes(file, geojson)
  const points = await (options?.buildPoints ?? pointsFromGeojson)(bytes, images)
  if (!points.length) throw new ArchiveError('no-features')
  return { file, points, imageCount: images.length }
}
